const detectBrowser = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('edg')) return 'edge';
  if (ua.includes('opr') || ua.includes('opera')) return 'opera';
  if (ua.includes('chrome')) return 'chrome';
  if (ua.includes('safari')) return 'safari';
  if (ua.includes('firefox')) return 'firefox';
  return 'unknown';
};

const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const normalizeRoleTokens = (...sources) => {
  const tokens = [];

  sources.forEach((source) => {
    if (source === null || source === undefined) {
      return;
    }

    if (typeof source === 'string') {
      source
        .split(/[\s,]+/)
        .map((value) => value.trim())
        .filter(Boolean)
        .forEach((value) => tokens.push(value.toUpperCase()));
      return;
    }

    if (Array.isArray(source)) {
      source
        .map((value) => String(value ?? '').trim().toUpperCase())
        .filter(Boolean)
        .forEach((value) => tokens.push(value));
      return;
    }

    const token = String(source).trim().toUpperCase();
    if (token) {
      tokens.push(token);
    }
  });

  return Array.from(new Set(tokens));
};

const isTrueLike = (value) => {
  if (value === true) {
    return true;
  }

  if (typeof value === 'string') {
    return ['1', 'TRUE', 'YES', 'ON', 'Y'].includes(value.trim().toUpperCase());
  }

  return false;
};

export const AUTH_STORAGE_KEYS = {
  accessToken: 'access_token',
  refreshToken: 'refresh_token',
  userNo: 'user_no',
  userNoLegacy: 'userNo',
  isAdmin: 'is_admin',
  deviceId: 'device_id',
  appNext: 'app_next',
};

export const isAppEnvironment = () => {
  return window.location.pathname.startsWith('/app');
};

export const getOrCreateDeviceId = () => {
  try {
    if (window.RN_DEVICE_ID) {
      const deviceId = window.RN_DEVICE_ID;
      window.localStorage.setItem(AUTH_STORAGE_KEYS.deviceId, deviceId);
      return deviceId;
    }

    let deviceId = window.localStorage.getItem(AUTH_STORAGE_KEYS.deviceId);
    if (!deviceId) {
      const browser = detectBrowser();
      const uuid = generateUUID().slice(0, 8);
      deviceId = `web_${browser}_${uuid}`;
      window.localStorage.setItem(AUTH_STORAGE_KEYS.deviceId, deviceId);
    }

    return deviceId;
  } catch (error) {
    console.error('[Auth] Failed to resolve device id.', error);
    return `web_unknown_${Date.now().toString(36)}`;
  }
};

export const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1] || '';
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export const isJwtExpired = (token, skewSeconds = 0) => {
  const payload = parseJwt(token || '');
  if (!payload?.exp) {
    return true;
  }

  const nowInSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= nowInSeconds + skewSeconds;
};

export const deriveAdminFlag = (source = {}) => {
  const roles = normalizeRoleTokens(
    source.roles,
    source.role,
    source.authorities,
    source.role_code,
    source.roleCode,
    source.scope,
  );

  if (roles.includes('ADMIN') || roles.includes('ROLE_ADMIN')) {
    return true;
  }

  if (isTrueLike(source.is_admin) || isTrueLike(source.isAdmin) || isTrueLike(source.admin)) {
    return true;
  }

  const roleId = source.role_id ?? source.roleId;
  if (roleId !== null && roleId !== undefined) {
    const numericRoleId = Number(roleId);
    if (Number.isFinite(numericRoleId) && numericRoleId === 1) {
      return true;
    }
  }

  return false;
};

export const persistAdminFlag = (isAdminValue) => {
  try {
    if (isAdminValue) {
      window.localStorage.setItem(AUTH_STORAGE_KEYS.isAdmin, 'true');
    } else {
      window.localStorage.removeItem(AUTH_STORAGE_KEYS.isAdmin);
    }
  } catch {
    // ignore storage failures
  }
};

export const resolveUserNo = (source = {}) => {
  const directUserNo = source.user_no ?? source.userNo;
  if (directUserNo !== null && directUserNo !== undefined && String(directUserNo).trim()) {
    return String(directUserNo).trim();
  }

  const token = source.access_token ?? source.accessToken ?? null;
  if (!token) {
    return null;
  }

  const payload = parseJwt(token);
  const subject = payload?.sub;
  if (!subject) {
    return null;
  }

  return String(subject).trim() || null;
};
